import { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Mail, LogOut, Snowflake } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import ProfileSuspensionModal from './ProfileSuspensionModal';
import { useState } from 'react';
import { pb } from '@/lib/pocketbase';

const FrozenTutorGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, logout } = useAuth();
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch tutor profile to check if frozen
  useEffect(() => {
    const checkFrozenStatus = async () => {
      if (user?.role === 'tutor' && user?.id) {
        try {
          const records = await pb.collection('tutors').getList(1, 1, {
            filter: `user = "${user.id}"`
          });
          
          if (records.items.length > 0) {
            setIsFrozen(records.items[0].isFrozen === true);
          }
        } catch (error) {
          console.error('Error checking frozen status:', error);
        }
      }
      setIsLoading(false);
    };

    checkFrozenStatus();
  }, [user]);
  
  // Check if user is a frozen tutor
  const isFrozenTutor = user?.role === 'tutor' && isFrozen === true;

  // Prevent scrolling when frozen modal is shown
  useEffect(() => {
    if (isFrozenTutor) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFrozenTutor]);

  const handleLogout = async () => {
    await logout();
  };

  // Show loading state while checking
  if (isLoading) {
    return <>{children}</>;
  }

  if (!isFrozenTutor) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Backdrop overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] pointer-events-none" />
      
      {/* Centered modal */}
      <div className="fixed inset-0 flex items-center justify-center z-[101] p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 space-y-6">
          {/* Icon and header */}
          <div className="text-center space-y-3">
            <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
              <Snowflake className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Account Frozen</h2>
              <p className="text-sm text-gray-600 mt-1">Your account access is temporarily restricted</p>
            </div>
          </div>

          {/* Warning message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900 space-y-2">
                <p className="font-semibold">We've been trying to reach you</p>
                <p>We have attempted to contact you multiple times via email and WhatsApp regarding important updates to your account.</p>
                <p className="font-medium">Please review your emails or contact us at:</p>
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded border border-blue-300">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <a 
                    href="mailto:support@tutornesta.com" 
                    className="text-blue-600 font-medium hover:underline"
                  >
                    support@tutornesta.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => setShowSuspensionModal(true)}
              variant="outline"
              className="w-full border-2 border-gray-300 hover:bg-gray-50"
            >
              Request Account Suspension or Deletion
            </Button>
            
            <Button
              onClick={handleLogout}
              variant="default"
              className="w-full bg-gray-900 hover:bg-gray-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Suspension modal */}
      <ProfileSuspensionModal
        isOpen={showSuspensionModal}
        onClose={() => setShowSuspensionModal(false)}
      />
    </>
  );
};

export default FrozenTutorGuard;
